import { useMemo } from 'react';
import { useLicenseInfo } from '../../../redux/slices/license/hooks/hooks';
import { useMyTeamRole } from '../../team/useMyTeamRole';
import { useOnboardingState } from '../../onboarding/useOnboardingState';
import { useMusic24Plan } from '../hooks/useMusic24Plan';

/** The "You are on Free now" pill shows for this many days after the trial ends (design h3). */
export const TRIAL_ENDED_SHOW_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

export type TrialStatus =
  | { kind: 'running'; days: number; hasSong: boolean }
  | { kind: 'lastDay'; days: number }
  | { kind: 'ended' };

/**
 * The trial status of the sidebar pill and the top-bar chip (design: onboarding h3). It reads the
 * same licence facts the old Home trial banner read. A member sees nothing: the owner runs the plan.
 * Returns null when no pill shows.
 */
export function useTrialStatus(now: Date = new Date()): TrialStatus | null {
  const licenseInfo = useLicenseInfo();
  const plan = useMusic24Plan();
  const isMember = useMyTeamRole() === 'MEMBER';
  const { state } = useOnboardingState();
  const songItem = state?.checklist?.items.find((item) => item.key === 'song');
  const hasSong = songItem?.done === true || (state?.firstReleaseUuid ?? null) !== null;
  const nowMs = now.getTime();

  return useMemo((): TrialStatus | null => {
    if (isMember) {
      return null;
    }
    const days = licenseInfo.trialDaysRemaining;
    if (plan === 'trial' && licenseInfo.isTrialActive && days !== null) {
      return days <= 1 ? { kind: 'lastDay', days } : { kind: 'running', days, hasSong };
    }
    const endsAt = licenseInfo.trialEndsAt;
    if (plan === 'free' && licenseInfo.trialStatus === 'expired' && endsAt !== null) {
      return nowMs - endsAt.getTime() < TRIAL_ENDED_SHOW_DAYS * DAY_MS ? { kind: 'ended' } : null;
    }
    return null;
  }, [hasSong, isMember, licenseInfo, nowMs, plan]);
}
